import {
  convertBase64ToBlob,
  convertBlobToBase64,
} from 'apps/studio/lib/utils/pdfData'
import { hexToHsva, hsvaToHsvaString } from '@uiw/color-convert'
import { getOperationType } from 'apps/builder/lib/utils'
import { convertToBuilderType } from 'apps/builder/lib/transforms'
import { initialState } from 'apps/studio/store/reducers/entities/pdf'
import { RECENT_COLOR_LIMIT } from 'apps/studio/lib/utils/color'

export const extractForm = ({
  entries = [],
  sheets = [],
  sections = [],
  conditions = [],
  operations = [],
  data_source_context_params = [],
  type,
  ...app
}) => {
  const entryGuidToSheetIdMap = new Map()
  const entryIdToEntryGuidMap = new Map()

  const processedEntries = entries.map(entry => {
    entryGuidToSheetIdMap.set(entry.guid, String(entry.sheet_id))
    entryIdToEntryGuidMap.set(entry.id, entry.guid)
    return {
      ...entry,
      type: convertToBuilderType(entry.type, entry.style),
    }
  })

  return {
    app,
    entries: processedEntries,
    sheets,
    sections,
    conditions,
    operations: operations.map(
      ({ value_entry_id, entry: entryGuid, ...operation }) => {
        const valueEntryGuid = value_entry_id
          ? entryIdToEntryGuidMap.get(value_entry_id)
          : null
        return {
          ...operation,
          value_entry_id,
          entry: entryGuid,
          sheet_id: entryGuidToSheetIdMap.get(entryGuid),
          ...(valueEntryGuid && {
            parentEntryGuid: valueEntryGuid,
            parentEntrySheetId: entryGuidToSheetIdMap.get(valueEntryGuid),
          }),
        }
      }
    ),
    dataSourceContextParams: data_source_context_params,
  }
}

// from api, format=flat
export const extractFlatOperations = operations =>
  operations.map(({ operation_type_id, value_entry, ...operation }, i) => {
    return {
      ...operation,
      operation_type_id,
      operation_type: getOperationType(operation_type_id),
      index: i,
    }
  })

export const extractPdf = pdfData => {
  if (!pdfData || !pdfData.id) return {}

  const { pages, elements, base64, position, ...pdfMetaData } = pdfData

  return {
    pdf: {
      ...initialState,
      position: position ? position : initialState.position,
      ...pdfMetaData,
      pdf_path: convertBase64ToBlob(base64),
    },
    pages,
    elements,
  }
}

export const unpackReferenceImageType = ({ reference_image: { id, name } }) => {
  return {
    id,
    name,
  }
}

export const extractColors = pdfData => {
  const { elements = [], pages = [], styles } = pdfData
  const colorMap = new Map()
  const allColors = [...elements, ...pages, { styles }]
  allColors.forEach(({ styles }) => {
    const { backgroundColor, backgroundColorOpacity, fontColor } = styles || {}
    if (backgroundColor) {
      const color = hexToHsva(backgroundColor)
      if (backgroundColorOpacity != null) {
        color.a = backgroundColorOpacity
      }
      const colorKey = hsvaToHsvaString(color)
      colorMap.set(colorKey, color)
    }
    if (fontColor) {
      // fonts do not have opacity atm
      const color = { ...hexToHsva(fontColor), a: 1 }
      const colorKey = hsvaToHsvaString(color)
      colorMap.set(colorKey, color)
    }
  })

  // only keep the 14 first colors
  return Array.from(colorMap.values()).slice(0, RECENT_COLOR_LIMIT)
}

export const assemblePdf = async (pdf, pages, elements) => {
  const { pdf_path, ...pdfMetaData } = pdf
  const response = await fetch(pdf_path)
  const blob = await response.blob()
  const base64String = await convertBlobToBase64(blob)

  return {
    ...pdfMetaData,
    base64: base64String,
    pages,
    elements,
  }
}
